import { IBookingTimesFare } from "./../../Interfaces/tours";
import { logger } from "@utilities";
import { AppDispatch } from "./../store";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    IDetailTour,
    ITourFare,
    ICheckAvailUrl,
    IFavoriteTour,
    ITourFareBookingTime,
} from "@interfaces";
import { toursApi } from "@api";
import { store } from "@redux";
import { RootState } from ".";
import { getProductHelper } from "src/Module/Checkout/productHelper";

interface IInitialState {
    tour: IDetailTour | null;
    tourFare: ICheckAvailUrl;
    isLoading: boolean;
}

const initialState: IInitialState = {
    tour: null,
    tourFare: {
        levy: null,
        bookingTimeId: null,
        productPricesDetailsId: null,
        fareName: null,
        dateRange: {
            from: null,
            to: null,
        },
    },
    isLoading: false,
};

export const getTour = createAsyncThunk<IDetailTour, string | number>(
    "tour/getTour",
    async (id: string | number, { rejectWithValue }) => {
        const favoriteTours = store.getState().favoriteTours.tours as IFavoriteTour[];
        try {
            const result = await getProductHelper(id.toString());
            if (!result.isSuccess) {
                throw new Error(result.errorMessage);
            }
            if (!result.data) {
                throw new Error("Product not found");
            }
            const isLiked = favoriteTours.some((tour) => tour.productId === id);
            return {
                ...result.data!,
                isLiked: isLiked,
            };
        } catch (err: any) {
            return rejectWithValue(err);
        }
    },
);

export const getFarePrice =
    (fareId: string | number, fareName: string) => async (dispatch: AppDispatch) => {
        try {
            const res = await toursApi.getBookingDetail(fareId);

            const bookingTimes: IBookingTimesFare[] = res?.data?.bookingTimes;
            //make default fare
            if (bookingTimes?.length <= 0) {
                bookingTimes.push({
                    BookingTimeID: "0",
                    BookingTime: "Default",
                    Default: true,
                });
            }

            dispatch(setFareBookingTime({ fareId, bookingTime: { ...res.data, bookingTimes } }));
        } catch {
            const tour = store.getState().tour.tour;
            if (tour) {
                logger.getFareError(tour.name, tour.productId, fareName, fareId.toString());
            }

            dispatch(
                setFareBookingTime({
                    fareId,
                    bookingTime: {
                        bookingTimes: [],
                        pickupLocations: [],
                        optionalFields: [],
                        bookingTimeError: true,
                    },
                }),
            );
        }
    };

const tourSplice = createSlice({
    name: "tour",
    initialState: initialState,
    reducers: {
        setTour: (state, action) => {
            state.tour = action.payload;
        },
        resetTour: (state) => {
            state.tour = null;
        },
        //set pickupTime item của faresprice
        setPickupTimeItem: (state, action) => {
            const index = state.tour?.faresPrices.findIndex(
                (faresPrice) => faresPrice.productPriceId === action.payload.productPriceId,
            );

            if (index !== undefined && index >= 0 && state.tour) {
                state.tour.faresPrices[index].pickupTime = action.payload.value;
            }
        },
        //reset pickup time
        resetPickupTimes: (state) => {
            state.tour?.faresPrices.forEach((faresPrice) => {
                faresPrice.pickupTime = "";
            });
        },
        setTourFare: (state, action) => {
            const { bookingTimeId, productPricesDetailsId, fareName, levy } = action.payload;
            state.tourFare.bookingTimeId = bookingTimeId;
            state.tourFare.productPricesDetailsId = productPricesDetailsId;
            state.tourFare.fareName = fareName;
            state.tourFare.levy = levy;
        },
        resetTourFare: (state) => {
            state.tourFare.bookingTimeId = null;
            state.tourFare.productPricesDetailsId = null;
            state.tourFare.fareName = null;
            state.tourFare.levy = null;
        },
        setDateRangeFrom: (state, action) => {
            state.tourFare.dateRange.from = action.payload;
            if (!state.tourFare.dateRange.to) {
                state.tourFare.dateRange.to = action.payload;
            }
        },
        setDateRangeTo: (state, action) => {
            state.tourFare.dateRange.to = action.payload;
        },
        setDateRange: (state, action) => {
            state.tourFare.dateRange.from = action.payload.from;
            state.tourFare.dateRange.to = action.payload.to;
        },
        resetDateRange: (state) => {
            state.tourFare.dateRange = {
                from: null,
                to: null,
            };
        },
        sortTours: (state, action: PayloadAction<ITourFare[]>) => {
            if (state.tour) {
                state.tour.faresPrices = action.payload;
            }
        },
        setFareBookingTime: (
            state,
            action: PayloadAction<{ bookingTime: ITourFareBookingTime; fareId: string | number }>,
        ) => {
            const { bookingTime, fareId } = action.payload;
            if (state.tour) {
                const fareIndex = state.tour.faresPrices.findIndex(
                    (fare) => fare.productPricesDetailsId === fareId,
                );

                const defaultTime = bookingTime.bookingTimes.find((time) => time.Default);

                state.tour.faresPrices[fareIndex] = {
                    ...state.tour.faresPrices[fareIndex],
                    ...bookingTime,
                    bookingTimeId:
                        defaultTime?.BookingTimeID || bookingTime.bookingTimes[0]?.BookingTimeID,
                };
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getTour.fulfilled, (state, action: { payload: IDetailTour }) => {
            state.tour = action.payload;
            state.isLoading = false;
        });

        builder.addCase(getTour.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(getTour.rejected, (state) => {
            state.tour = null;
            state.isLoading = false;
        });
    },
});

export const selectTour = (state: RootState) => state.tour;

export const {
    resetTour,
    setTour,
    setTourFare,
    resetTourFare,
    setDateRangeFrom,
    setDateRangeTo,
    resetDateRange,
    sortTours,
    setDateRange,
    setPickupTimeItem,
    resetPickupTimes,
    setFareBookingTime,
} = tourSplice.actions;
export default tourSplice.reducer;
